import React, {useEffect, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {
  SettingsBannersEnum,
  useSettingsBanners,
} from '../../../../../libs/shared/core/src/helpers/react/hooks/useSettingsBanners';
import {DecisionTreeModel} from '../../../../../libs/shared/core/src/models/api/decisionTrees/decisionTreeModel';
import {logger} from '../../../../../libs/shared/core/src/services/logger/logger';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {UpdateDecisionTree, updateDecisionTree} from '../../../../settings/chatbots/settingsChatBots/api';
import {useApplicationsState} from '../../../../settings/customFields/useApplications';
import {useSettingsCustomFields} from '../../../../settings/customFields/useCustomFields';
import {useFetchDecisionTree} from '../../../../settings/fetchers/decisionTrees';
import {AppState} from '../../../../store/appState';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {makeGetChatbotHasUnavailableFeatures} from '../../../../store/data/decisionTrees/chatBotSelectors';
import {castAsPreExpressions} from '../../flowBuilder/expressionHelpers';
import {defaultChatBotBuilderSubtreeState, defaultChatBotBuilderTreeState} from '../defaultTree';
import {defaultChatBotExpressions} from '../expressions/expressionHelpers';
import {validateChatBotBuilderTreeData} from '../validators';
import {useChatBotBuilderStore} from './useChatBotBuilderStore';

/*
 * Hooks.
 */

/**
 * Hook for saving, canceling, resetting chatbot store edits.
 */
export function useEditChatBotState(decisionTreeId: number | undefined) {
  const companyUrl = useAppSelector(getCompanyUrl);
  const {decisionTree, refetch} = useFetchDecisionTree(decisionTreeId, {enabled: Boolean(decisionTreeId)});

  const {customFields} = useSettingsCustomFields();
  const applicationsState = useApplicationsState();

  const isSubtree = decisionTree?.treeType === 'subtree';
  const [chatBot, setChatBot] = useState<DecisionTreeModel>();
  const [isActive, setIsActive] = useState(isSubtree); // subtrees are always active by default
  const {showBanner} = useSettingsBanners();

  const chatBotBuilderStore = useChatBotBuilderStore(
    defaultChatBotBuilderSubtreeState.nodes,
    defaultChatBotBuilderSubtreeState.edges,
    defaultChatBotExpressions,
    customFields,
    applicationsState,
  );

  const isDowngradeRequired = useAppSelector((state: AppState) => {
    if (!chatBotBuilderStore.treeData) {
      return false;
    }
    return makeGetChatbotHasUnavailableFeatures(chatBotBuilderStore.treeData)(state);
  });

  const previousDecisionTree = usePrevious(decisionTree);
  useEffect(() => {
    if (!decisionTree || decisionTree === previousDecisionTree) {
      return;
    }

    setChatBot(decisionTree);
    setIsActive(decisionTree.isActive);

    chatBotBuilderStore.expressionsEditor.actions.onReplaceExpressions(
      castAsPreExpressions(decisionTree.expressions),
    );

    resetChatBotFlow();
  });

  useEffect(() => {
    if (isDowngradeRequired) {
      setIsActive(false);
    }
  }, [isDowngradeRequired]);

  const resetChatBotFlow = (forceRefreshToDefaultState = false) => {
    if (decisionTree?.treeData && !forceRefreshToDefaultState) {
      chatBotBuilderStore.setNodes(decisionTree.treeData.nodes.concat());
      chatBotBuilderStore.setEdges(decisionTree.treeData.edges.concat());

      chatBotBuilderStore.expressionsEditor.actions.onReplaceExpressions(
        castAsPreExpressions(decisionTree.expressions),
      );
    } else {
      // Depending on if their billing has access to advanced features we want to have different
      // default states for the bot.
      const state = defaultChatBotBuilderTreeState;
      chatBotBuilderStore.setNodes(state.nodes);
      chatBotBuilderStore.setEdges(state.edges);

      chatBotBuilderStore.expressionsEditor.actions.onReplaceExpressions(defaultChatBotExpressions);
    }

    // If they are force refreshing, we should mark the state as dirty.
    if (forceRefreshToDefaultState) {
      chatBotBuilderStore.setIsDirty(true);
      chatBotBuilderStore.setSelectedNode(undefined);
    }
  };

  const onCancel = () => {
    setIsActive(Boolean(decisionTree?.isActive));
    chatBotBuilderStore.setIsDirty(false);
    resetChatBotFlow();
    chatBotBuilderStore.history.clear();
  };

  const onSaveAsync = async (name?: string) => {
    if (!chatBot) {
      return undefined;
    }
    const {treeData, expressionsEditor} = chatBotBuilderStore;

    const {expressions} = expressionsEditor.state;

    const updateData: UpdateDecisionTree = {
      name: name ?? chatBot.name,
      tree_data: treeData,
      active: isActive,
      expressions,
    };

    chatBotBuilderStore.setIsDirty(false);

    try {
      validateChatBotBuilderTreeData(treeData);
      const updatedChatBot = await updateDecisionTree(companyUrl, chatBot.id, updateData);
      refetch();

      trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_CHAT_BOTS_BUILDER_SAVE_CLICK, {
        decision_tree_id: chatBot.id,
      });

      // If a bot has successfully been updated and refetched, clear the builder history.
      chatBotBuilderStore.history.clear();

      return updatedChatBot;
    } catch (err) {
      // Message exists if there is a genuine validation issue. We have created a bad tree, log this.
      if (err.message) {
        logger.error(err.message, err.data);
      }

      const bannerMessage = err.reason ?? (
        <Trans id="settings-chat-bots-builder-validation-error">Error saving chatbot</Trans>
      );

      showBanner({type: SettingsBannersEnum.ERROR, message: bannerMessage});
      chatBotBuilderStore.setIsDirty(true);
      return undefined;
    }
  };

  return {
    store: chatBotBuilderStore,
    isActive,
    chatBot,
    customFields,
    setIsActive,
    onCancel,
    onSaveAsync,
    resetChatBotFlow,
  };
}
